import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import SectionBackground from '../components/SectionBackground';

// Stripe payment links (TM RUSH account). Prices: TM RUSH Client Fee Schedule 2026 (October 2026).
const CHECKOUT = {
  company: 'https://buy.stripe.com/7sY5kD2qs5UU1sifUd6EU04',
  individual: 'https://buy.stripe.com/cNibJ19SU3MM2wm37r6EU05',
};

const RATES = {
  company: { first: 530, additional: 430, professionalFirst: 350, professionalAdditional: 250, official: 180 },
  individual: { first: 440, additional: 340, professionalFirst: 350, professionalAdditional: 250, official: 90 },
};

const GOOGLE_ADS_QUOTE_CONVERSION = 'AW-16614537746/hTCFCPLsj7wcEJL0tvI9';

// Bank-transfer requests go to the TM RUSH n8n flow, which issues the invoice PDF for approval.
// EmailJS below stays as the fallback if this endpoint is unreachable.
const INVOICE_REQUEST_URL = 'https://n8n-hag0.srv1897462.hstgr.cloud/webhook/tmr-invoice-request';

const usd = (n) => `USD ${n.toLocaleString('en-US')}`;

const included = [
  'A quick search for identical marks before we file',
  'Review of your classes and list of products and services',
  "Filing at INPI, Brazil's trademark office, by our local team",
  'Official filing receipt and application number',
  'Updates on your application until the office decides',
  'Registration certificate (digital) once approved',
  '12 months of Brazil Trademark Watch',
];

const assurances = [
  { title: 'Simple Power of Attorney', text: 'Sign it as a PDF. No notary, no legalization, no apostille.' },
  { title: 'Filed within 24 business hours', text: 'After we receive your signed Power of Attorney and your payment.' },
  { title: 'No hidden fees', text: 'Nothing extra to pay when your trademark is approved. The certificate is included.' },
];

const trackEvent = (name, params = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...params });
  if (typeof window.gtag === 'function') window.gtag('event', name, params);
};

const trackInvoiceRequest = (email) => {
  trackEvent('invoice_request_submitted');
  if (typeof window.gtag !== 'function') return;
  const normalized = email?.trim().toLowerCase();
  if (normalized) window.gtag('set', 'user_data', { email: normalized });
  window.gtag('event', 'conversion', { send_to: GOOGLE_ADS_QUOTE_CONVERSION });
};

const OptionButton = ({ active, onClick, title, text }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`text-left rounded-2xl border-2 p-5 transition-colors w-full ${
      active ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white hover:border-primary/40'
    }`}
  >
    <span className="flex items-center gap-3 mb-1">
      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? 'border-primary' : 'border-gray-300'}`}>
        {active && <span className="w-2.5 h-2.5 rounded-full bg-primary" />}
      </span>
      <span className="font-bold text-text">{title}</span>
    </span>
    <span className="block text-sm text-text/70 pl-8">{text}</span>
  </button>
);

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-text mb-2">
      {label}
      {required && ' *'}
    </span>
    {children}
  </label>
);

const inputClass =
  'w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary bg-white';

const OrderPage = () => {
  const [applicant, setApplicant] = useState('company');
  const [classes, setClasses] = useState(1);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoice, setInvoice] = useState({
    payer: '', contact: '', email: '', address: '', country: '', taxId: '',
    trademark: '', classList: '', owner: '', notes: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Order Online | Brazil Trademark Filing | TM RUSH';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const r = RATES[applicant];
  const extra = classes - 1;
  const total = r.first + extra * r.additional;
  const professional = r.professionalFirst + extra * r.professionalAdditional;
  const official = classes * r.official;

  const checkoutUrl = `${CHECKOUT[applicant]}?locale=en&client_reference_id=web-${applicant}-${classes}c`;

  const setField = (e) => setInvoice((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submitInvoice = async (e) => {
    e.preventDefault();
    const required = ['payer', 'contact', 'email', 'address', 'country', 'trademark', 'classList'];
    if (required.some((k) => !invoice[k].trim())) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoice.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setSending(true);
    setError('');
    try {
      let delivered = false;
      try {
        const res = await fetch(INVOICE_REQUEST_URL, {
          method: 'POST',
          body: new URLSearchParams({ ...invoice, applicant, classes: String(classes) }),
        });
        const data = await res.json().catch(() => ({}));
        delivered = res.ok && data.ok === true;
      } catch (err) {
        delivered = false;
      }
      if (delivered) {
        trackInvoiceRequest(invoice.email);
        setSent(true);
        return;
      }
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
      if (!serviceId || !templateId || !publicKey) throw new Error('EmailJS is not configured');
      emailjs.init(publicKey);
      const summary = [
        'INVOICE REQUEST — ORDER ONLINE (wire transfer)',
        `Applicant type: ${applicant === 'company' ? 'Company' : 'Individual (50% reduced official fee)'}`,
        `Classes: ${classes} (${invoice.classList})`,
        `Total: ${usd(total)} (professional ${usd(professional)} + estimated official ${usd(official)})`,
        '',
        `Payer: ${invoice.payer}`,
        `Contact: ${invoice.contact}`,
        `Email: ${invoice.email}`,
        `Billing address: ${invoice.address}`,
        `Country: ${invoice.country}`,
        `Tax ID: ${invoice.taxId || 'not informed'}`,
        `Trademark owner: ${invoice.owner || 'same as payer'}`,
        `Notes: ${invoice.notes || '-'}`,
      ].join('\n');
      const response = await emailjs.send(serviceId, templateId, {
        to_email: 'tmrush.br@gmail.com',
        from_name: `${invoice.contact} (${invoice.payer})`,
        from_email: invoice.email,
        reply_to: invoice.email,
        trademark: `[INVOICE REQUEST] ${invoice.trademark}`,
        phone: 'Not informed',
        class: `${classes} class(es): ${invoice.classList}`,
        description: summary,
      });
      if (response.status !== 200) throw new Error(`Status ${response.status}`);
      trackInvoiceRequest(invoice.email);
      setSent(true);
    } catch (err) {
      setError('We could not send your request. Please try again or write to contact@tmrush.com.');
    } finally {
      setSending(false);
    }
  };

  const includedCard = (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-text mb-4">Included in every filing</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {included.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-text/80">
                        <span className="text-primary font-bold" aria-hidden="true">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-text/60 mt-5">
                    Replies to objections from the office, oppositions and appeals are quoted separately. Prices assume a
                    standard list of products and services (pre-approved by INPI).
                  </p>
                </div>
  );

  return (
    <div className="bg-background">
      <SectionBackground image="/images/bg-hero.svg" overlay="dark" className="pt-36 md:pt-40 pb-16 md:pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <p className="text-primary-light font-semibold text-sm uppercase tracking-wider mb-4">Order online</p>
            <h1 className="text-4xl md:text-5xl font-bold mb-5 leading-tight">File your trademark in Brazil</h1>
            <p className="text-lg text-gray-200 leading-relaxed">
              One fixed price that already includes the Brazilian government fee. We file within 24 business hours after
              you sign the Power of Attorney, and we watch your brand for 12 months at no extra cost. Pay by card now, or
              pay by bank transfer and we send you an invoice.
            </p>
          </div>
        </div>
      </SectionBackground>

      <section className="py-14 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Configurator */}
            <div className="lg:col-span-7 space-y-6">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
                <p className="text-primary font-semibold text-sm mb-1">Step 1</p>
                <h2 className="text-2xl font-bold text-text mb-5">Who will own the trademark?</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <OptionButton
                    active={applicant === 'company'}
                    onClick={() => setApplicant('company')}
                    title="A company"
                    text={`From ${usd(RATES.company.first)} · government fee ${usd(RATES.company.official)} per class`}
                  />
                  <OptionButton
                    active={applicant === 'individual'}
                    onClick={() => setApplicant('individual')}
                    title="An individual"
                    text={`From ${usd(RATES.individual.first)} · government fee 50% lower`}
                  />
                </div>
                {applicant === 'individual' && (
                  <p className="text-sm text-text/70 mt-4 bg-background-auxiliary rounded-xl p-4">
                    The lower government fee is for people applying in their own name who do not own a company in the
                    same business. If this does not apply, the difference ({usd(90)} per class) is due before we file.
                  </p>
                )}
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
                <p className="text-primary font-semibold text-sm mb-1">Step 2</p>
                <h2 className="text-2xl font-bold text-text mb-2">How many classes?</h2>
                <p className="text-text/70 mb-5">
                  Classes are categories of products and services. Each class is a separate application in Brazil. Not
                  sure? Choose 1. We check the classes with you before we file.
                </p>
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    aria-label="Fewer classes"
                    onClick={() => setClasses((c) => Math.max(1, c - 1))}
                    className="w-12 h-12 rounded-xl border-2 border-gray-200 text-2xl font-bold text-text hover:border-primary disabled:opacity-40"
                    disabled={classes === 1}
                  >
                    −
                  </button>
                  <span className="text-3xl font-bold text-text w-14 text-center" aria-live="polite">{classes}</span>
                  <button
                    type="button"
                    aria-label="More classes"
                    onClick={() => setClasses((c) => Math.min(45, c + 1))}
                    className="w-12 h-12 rounded-xl border-2 border-gray-200 text-2xl font-bold text-text hover:border-primary"
                  >
                    +
                  </button>
                  <span className="text-text/70">{classes === 1 ? 'class' : 'classes'}</span>
                </div>
                <p className="text-sm text-text/60 mt-4">
                  Need help choosing classes?{' '}
                  <Link to="/request-proposal" className="text-primary font-semibold hover:text-primary-hover">
                    Ask for a free proposal
                  </Link>
                </p>
              </div>

              <div className="hidden lg:block">{includedCard}</div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="bg-text text-white rounded-3xl shadow-lg p-6 md:p-8">
                <h2 className="text-xl font-bold mb-5">Your order</h2>
                <div className="space-y-3 text-gray-200">
                  <div className="flex justify-between gap-4">
                    <span>Trademark filing, 1st class</span>
                    <span className="font-semibold text-white whitespace-nowrap">{usd(r.first)}</span>
                  </div>
                  {extra > 0 && (
                    <div className="flex justify-between gap-4">
                      <span>
                        Extra {extra === 1 ? 'class' : 'classes'} × {extra}
                      </span>
                      <span className="font-semibold text-white whitespace-nowrap">{usd(extra * r.additional)}</span>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <span>12 months of Brazil Trademark Watch</span>
                    <span className="font-semibold text-primary-light whitespace-nowrap">Included</span>
                  </div>
                </div>
                <div className="border-t border-white/15 mt-5 pt-5 flex justify-between items-baseline gap-4">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-4xl font-bold whitespace-nowrap">{usd(total)}</span>
                </div>
                <p className="text-xs text-gray-300 mt-2">
                  Our fees {usd(professional)} + Brazilian government fees {usd(official)}. Nothing more to pay when your
                  trademark is approved.
                </p>

                <a
                  href={checkoutUrl}
                  onClick={() => trackEvent('begin_checkout', { value: total, currency: 'USD', items: classes })}
                  className="block text-center bg-primary text-white font-semibold text-lg rounded-xl px-6 py-4 mt-6 hover:bg-primary-hover shadow-md"
                >
                  Pay by card · {usd(total)}
                </a>
                <p className="text-xs text-gray-300 mt-3 leading-relaxed">
                  Secure payment by Stripe. On the next page you confirm your trademark, classes and billing details
                  {extra > 0 ? `, and add ${extra} extra ${extra === 1 ? 'class' : 'classes'}` : ''}. Your invoice arrives
                  by email automatically.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setShowInvoice(true);
                    setTimeout(() => document.getElementById('invoice')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
                  }}
                  className="w-full text-center bg-white/10 border border-white/25 text-white font-semibold rounded-xl px-6 py-3 mt-5 hover:bg-white/20"
                >
                  Pay by bank transfer
                </button>
                <p className="text-xs text-gray-300 mt-2 text-center">We email you an invoice with our bank details.</p>
              </div>

              <p className="text-sm text-text/70 mt-5 text-center">
                Already filed in Brazil?{' '}
                <Link to="/brazil-watch" className="text-primary font-semibold hover:text-primary-hover">
                  Watch your brand only · USD 150/year →
                </Link>
              </p>
              <div className="lg:hidden mt-6">{includedCard}</div>
            </div>
          </div>

          {/* Invoice request */}
          {showInvoice && (
            <div id="invoice" className="scroll-mt-28 mt-10 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-10 max-w-4xl">
              {!sent ? (
                <>
                  <h2 className="text-2xl md:text-3xl font-bold text-text mb-2">Pay by bank transfer</h2>
                  <p className="text-text/70 mb-6">
                    Tell us who is paying. We will email an invoice for {usd(total)} ({classes}{' '}
                    {classes === 1 ? 'class' : 'classes'}, {applicant === 'company' ? 'company' : 'individual'} price) with
                    our bank details. We start as soon as the payment arrives.
                  </p>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>
                  )}
                  <form onSubmit={submitInvoice} noValidate className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Who is paying? (company or full name)" required>
                      <input name="payer" value={invoice.payer} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Contact name" required>
                      <input name="contact" value={invoice.contact} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Email to receive the invoice" required>
                      <input type="email" name="email" value={invoice.email} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Tax number (VAT, EIN, RFC…)">
                      <input name="taxId" value={invoice.taxId} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Billing address" required>
                      <input name="address" value={invoice.address} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Country" required>
                      <input name="country" value={invoice.country} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Trademark (the name to register)" required>
                      <input name="trademark" value={invoice.trademark} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Classes (e.g. 9, 35, 42)" required>
                      <input name="classList" value={invoice.classList} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Trademark owner, if different from the payer">
                      <input name="owner" value={invoice.owner} onChange={setField} className={inputClass} />
                    </Field>
                    <Field label="Notes">
                      <input name="notes" value={invoice.notes} onChange={setField} className={inputClass} />
                    </Field>
                    <div className="md:col-span-2">
                      <button
                        type="submit"
                        disabled={sending}
                        className="w-full md:w-auto bg-primary text-white px-10 py-4 rounded-xl hover:bg-primary-hover font-semibold shadow-md disabled:opacity-50"
                      >
                        {sending ? 'Sending…' : `Send me the invoice · ${usd(total)}`}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="text-5xl text-primary mb-4">✓</div>
                  <h2 className="text-2xl md:text-3xl font-bold text-text mb-3">Request received</h2>
                  <p className="text-text/70 max-w-xl mx-auto">
                    Within one business day we will email your invoice with our bank details, together with the Power of
                    Attorney to sign. Questions: contact@tmrush.com
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Assurances */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {assurances.map((a) => (
              <div key={a.title} className="bg-background-auxiliary rounded-2xl p-6">
                <h3 className="font-bold text-text mb-1">{a.title}</h3>
                <p className="text-sm text-text/70">{a.text}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-text/60 mt-8 text-center">
            Prices from the TM RUSH Client Fee Schedule 2026. Law firms and IP firms:{' '}
            <a href="mailto:contact@tmrush.com?subject=Partner%20prices" className="text-primary font-semibold">
              ask for our partner prices
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
};

export default OrderPage;
